import { metricSeverity } from "../utils/metricSeverity";
import { useEffect, useState } from "react";
import { getCloudServers, getCloudHistory } from "../services/api";

export const cloudOnline = (server) => {
  const time = Date.parse(server.last_updated);
  return Number.isFinite(time) && Date.now() - time < Math.max(30000, (server.snapshot?.interval_seconds || 5) * 3000);
};
export function cloudTone(server) {
  if (!cloudOnline(server)) return "offline";
  const s = server.snapshot;
  const values = [s?.cpu?.usage_percent, s?.memory?.usage_percent, ...(s?.disks || []).map(d => d.usage_percent)];
  const severities = values.map(metricSeverity);
  if (severities.includes("critical")) return "critical";
  return severities.includes("warning") ? "warning" : "normal";
}

export default function useCloudMonitoring(hours) {
  const [servers, setServers] = useState([]);
  const [history, setHistory] = useState({});
  const [error, setError] = useState("");
  const [historyError, setHistoryError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  useEffect(() => {
    let cancelled = false, busy = false;
    const historyFetchedAt = new Map();
    setHistory({});
    const refresh = async () => {
      if (busy || cancelled) return;
      busy = true;
      try {
        const data = await getCloudServers();
        if (cancelled) return;
        setServers(data);
        setUpdatedAt(new Date());
        setError("");
        let failedHistory = false;
        const snapshots = {};
        await Promise.all(data.map(async server => {
          if (Date.now() - (historyFetchedAt.get(server.id) || 0) < 60000) return;
          try {
            snapshots[server.id] = await getCloudHistory(server.id, hours);
            historyFetchedAt.set(server.id, Date.now());
          } catch { failedHistory = true; }
        }));
        if (cancelled) return;
        setHistoryError(failedHistory ? "Histórico indisponível para um ou mais servidores. Tentando novamente." : "");
        setHistory(previous => {
          const next = {};
          const cutoff = Date.now() - hours * 3600000;
          for (const server of data) {
            const points = (snapshots[server.id] || previous[server.id] || []).filter(p => Date.parse(p.recorded_at) >= cutoff);
            const time = Date.parse(server.last_updated);
            if (time >= cutoff && time > Date.parse(points.at(-1)?.recorded_at || 0)) {
              points.push({ recorded_at: server.last_updated, cpu_usage: server.snapshot.cpu.usage_percent,
                memory_usage: server.snapshot.memory.usage_percent,
                network_rx_bps: server.snapshot.network.rx_bytes_per_second,
                network_tx_bps: server.snapshot.network.tx_bytes_per_second });
            }
            next[server.id] = points.slice(-1500);
          }
          return next;
        });
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || "Não foi possível consultar o monitoramento cloud. Os últimos dados foram mantidos.");
      } finally { busy = false; }
    };
    refresh();
    const interval = setInterval(refresh, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [hours]);
  return { servers, history, error, historyError, updatedAt };
}
